import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AuthModal from '../components/AuthModal';
import {
    getUserSubscription,
    cancelSubscriptionViaStripe,
    redirectToStripe,
    PLAN_CONFIGS,
    UserSubscription,
    BillingCycle,
} from '../services/subscriptionService';
import {
    CreditCard,
    SignOut,
    Warning,
    Check,
    ArrowRight,
    Crown,
    Star,
    Lightning,
    Buildings,
    House,
    CalendarBlank,
    ArrowsClockwise,
    XCircle,
} from '@phosphor-icons/react';

/* ─── Page Layout ─── */
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  padding: 7rem 1.5rem 4rem;

  @media (min-width: 768px) {
    padding: 8rem 2rem 4rem;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 0.5rem;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  color: #8b949e;
  font-size: 1rem;
  margin-bottom: 2.5rem;
`;

/* ─── Cards ─── */
const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 1.75rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    padding: 2rem 2.5rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  svg {
    color: #3AB8FF;
  }
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
`;

/* ─── Plan Badge ─── */
const PlanBadge = styled.div<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${p => p.$isActive ? 'rgba(58, 183, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${p => p.$isActive ? 'rgba(58, 183, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  color: ${p => p.$isActive ? '#3AB8FF' : '#8b949e'};
`;

const StatusDot = styled.span<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p =>
        p.$status === 'active' ? '#34D399' :
            p.$status === 'cancelled' ? '#f85149' :
                p.$status === 'past_due' ? '#F59E0B' : '#8b949e'};
  box-shadow: 0 0 6px ${p =>
        p.$status === 'active' ? 'rgba(52, 211, 153, 0.5)' :
            p.$status === 'cancelled' ? 'rgba(248, 81, 73, 0.5)' :
                p.$status === 'past_due' ? 'rgba(245, 158, 11, 0.5)' : 'none'};
`;

/* ─── Info Row ─── */
const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #8b949e;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoValue = styled.span`
  color: #f0f6fc;
  font-weight: 500;
  font-size: 0.9rem;
`;

/* ─── Action Buttons ─── */
const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #3AB8FF;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #50c1ff;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const DangerButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: rgba(248, 81, 73, 0.1);
  color: #f85149;
  border: 1px solid rgba(248, 81, 73, 0.2);
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(248, 81, 73, 0.2);
    border-color: rgba(248, 81, 73, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.04);
  color: #8b949e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: white;
  }
`;

/* ─── Plans Grid ─── */
const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PlanCard = styled.div<{ $isCurrent?: boolean; $isPopular?: boolean }>`
  padding: 1.5rem;
  background: ${p => p.$isCurrent ? 'rgba(52, 211, 153, 0.06)' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${p =>
        p.$isCurrent ? 'rgba(52, 211, 153, 0.25)' :
            p.$isPopular ? 'rgba(58, 183, 255, 0.25)' :
                'rgba(255, 255, 255, 0.06)'};
  border-radius: 16px;
  transition: all 0.2s;
  position: relative;

  ${p => !p.$isCurrent && `
    &:hover {
      border-color: rgba(58, 183, 255, 0.3);
      background: rgba(58, 183, 255, 0.03);
    }
  `}
`;

const PlanCardName = styled.div`
  font-weight: 700;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PlanCardPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #3AB8FF;
  margin: 0.75rem 0;

  span {
    font-size: 0.85rem;
    font-weight: 400;
    color: #8b949e;
  }
`;

const PlanCardTagline = styled.div`
  font-size: 0.8rem;
  color: #8b949e;
  margin-bottom: 0.75rem;
`;

const CurrentBadge = styled.span`
  font-size: 0.7rem;
  padding: 0.2rem 0.6rem;
  background: rgba(52, 211, 153, 0.15);
  color: #34D399;
  border-radius: 6px;
  font-weight: 600;
`;

const PopularBadge = styled.span`
  font-size: 0.7rem;
  padding: 0.2rem 0.6rem;
  background: rgba(58, 183, 255, 0.15);
  color: #3AB8FF;
  border-radius: 6px;
  font-weight: 600;
`;

const PlanSelectButton = styled.button<{ $isCurrent?: boolean }>`
  width: 100%;
  padding: 0.6rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: ${p => p.$isCurrent ? 'default' : 'pointer'};
  transition: all 0.2s;
  border: none;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  background: ${p => p.$isCurrent ? 'rgba(52, 211, 153, 0.1)' : '#3AB8FF'};
  color: ${p => p.$isCurrent ? '#34D399' : 'white'};

  ${p => !p.$isCurrent && `
    &:hover {
      background: #50c1ff;
      transform: translateY(-1px);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

/* ─── Modal ─── */
const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: #1c1c1c;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  padding: 2rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ModalText = styled.p`
  color: #8b949e;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

/* ─── Billing Toggle ─── */
const BillingToggle = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 1.5rem;
  max-width: 280px;
`;

const BillingOption = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${p => p.$isActive ? '#3AB8FF' : 'transparent'};
  color: ${p => p.$isActive ? 'white' : '#8b949e'};

  &:hover {
    color: ${p => p.$isActive ? 'white' : '#f0f6fc'};
  }
`;

/* ─── Empty State ─── */
const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;

  svg {
    color: #3AB8FF;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #8b949e;
    margin-bottom: 1.5rem;
  }
`;

const SuccessMessage = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: rgba(52, 211, 153, 0.08);
  border: 1px solid rgba(52, 211, 153, 0.2);
  border-radius: 12px;
  color: #34D399;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

/* ─── Component ─── */
const getPlanIcon = (planId: string) => {
    switch (planId) {
        case 'home': return <House weight="duotone" size={20} />;
        case 'plus': return <Star weight="duotone" size={20} />;
        case 'pro': return <Crown weight="duotone" size={20} />;
        case 'enterprise': return <Buildings weight="duotone" size={20} />;
        default: return <Lightning weight="duotone" size={20} />;
    }
};

const AccountPage: React.FC = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [subscription, setSubscription] = useState<UserSubscription | null>(null);
    const [loading, setLoading] = useState(true);
    const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [cancelSuccess, setCancelSuccess] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

    // Load subscription data
    useEffect(() => {
        if (user?.uid) {
            loadSubscription();
        } else {
            setLoading(false);
        }
    }, [user]);

    const loadSubscription = async () => {
        try {
            setLoading(true);
            const sub = await getUserSubscription(user!.uid);
            setSubscription(sub);
        } catch (error) {
            console.error('Error loading subscription:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!user || !subscription) return;

        const stripeSubId = (subscription as any).stripeSubscriptionId;
        if (!stripeSubId) {
            alert('No active Stripe subscription found.');
            return;
        }

        setCancelLoading(true);
        const result = await cancelSubscriptionViaStripe({
            userId: user.uid,
            subscriptionId: stripeSubId,
        });
        setCancelLoading(false);

        if (result.success) {
            setCancelSuccess(true);
            setShowCancelModal(false);
            await loadSubscription();
        } else {
            alert(result.message || 'Failed to cancel. Please contact support.');
        }
    };

    const handlePlanSelect = async (planId: string) => {
        if (!user) {
            setIsAuthModalOpen(true);
            return;
        }

        if (planId === 'starter') return;
        if (planId === 'enterprise') {
            window.location.href = 'mailto:info@sparkskool.com?subject=Enterprise Plan Inquiry';
            return;
        }

        setCheckoutLoading(planId);
        try {
            await redirectToStripe(
                planId as 'home' | 'plus' | 'pro',
                billingCycle,
                user.uid,
                user.email || undefined
            );
        } finally {
            setCheckoutLoading(null);
        }
    };

    const formatDate = (date: any) => {
        if (!date) return '—';
        const d = date?.toDate ? date.toDate() : new Date(date);
        return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };

    // Not signed in
    if (!user && !loading) {
        return (
            <PageWrapper>
                <Container>
                    <EmptyState>
                        <CreditCard size={48} weight="duotone" />
                        <h3>Sign in to manage your subscription</h3>
                        <p>Access your plan details, change your subscription, or cancel anytime.</p>
                        <PrimaryButton onClick={() => setIsAuthModalOpen(true)}>
                            Sign In
                            <ArrowRight size={16} />
                        </PrimaryButton>
                    </EmptyState>
                    <AuthModal
                        isOpen={isAuthModalOpen}
                        onClose={() => setIsAuthModalOpen(false)}
                        onSuccess={() => { setIsAuthModalOpen(false); }}
                    />
                </Container>
            </PageWrapper>
        );
    }

    // Loading
    if (loading) {
        return (
            <PageWrapper>
                <Container>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
                        <ArrowsClockwise size={32} style={{ animation: 'spin 1s linear infinite', color: '#3AB8FF' }} />
                    </div>
                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </Container>
            </PageWrapper>
        );
    }

    const currentPlanId = subscription?.planId || 'starter';
    const currentPlan = PLAN_CONFIGS[currentPlanId] || PLAN_CONFIGS.starter;

    return (
        <PageWrapper>
            <Container>
                <PageTitle>My Account</PageTitle>
                <PageSubtitle>Manage your subscription and account settings.</PageSubtitle>

                {/* ── Current Plan Card ── */}
                <Card>
                    <CardHeader>
                        <CreditCard size={22} weight="duotone" />
                        <CardTitle>Current Plan</CardTitle>
                    </CardHeader>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                        <PlanBadge $isActive={subscription?.status === 'active'}>
                            {getPlanIcon(currentPlanId)}
                            {currentPlan.name}
                        </PlanBadge>
                        {subscription && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                                <StatusDot $status={subscription.status} />
                                <span style={{ color: '#8b949e', textTransform: 'capitalize' }}>{subscription.status}</span>
                            </div>
                        )}
                    </div>

                    {subscription && subscription.planId !== 'starter' ? (
                        <>
                            <InfoRow>
                                <InfoLabel><CalendarBlank /> Billing Cycle</InfoLabel>
                                <InfoValue style={{ textTransform: 'capitalize' }}>{subscription.billingCycle}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel><CalendarBlank /> Current Period End</InfoLabel>
                                <InfoValue>{formatDate(subscription.endDate)}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel><ArrowsClockwise /> Auto-Renew</InfoLabel>
                                <InfoValue style={{ color: subscription.autoRenew ? '#34D399' : '#f85149' }}>
                                    {subscription.autoRenew ? 'Yes' : 'No'}
                                </InfoValue>
                            </InfoRow>

                            {cancelSuccess && (
                                <SuccessMessage
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <Check weight="bold" size={18} />
                                    Subscription cancelled. Access continues until {formatDate(subscription.endDate)}.
                                </SuccessMessage>
                            )}

                            <ButtonRow>
                                {subscription.status === 'active' && subscription.autoRenew && (
                                    <DangerButton onClick={() => setShowCancelModal(true)}>
                                        <XCircle size={18} />
                                        Cancel Subscription
                                    </DangerButton>
                                )}
                                <GhostButton onClick={logout}>
                                    <SignOut size={18} />
                                    Sign Out
                                </GhostButton>
                            </ButtonRow>
                        </>
                    ) : (
                        <>
                            <p style={{ color: '#8b949e', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                You're on the free Starter plan. Upgrade to unlock more features.
                            </p>
                            <ButtonRow>
                                <GhostButton onClick={logout}>
                                    <SignOut size={18} />
                                    Sign Out
                                </GhostButton>
                            </ButtonRow>
                        </>
                    )}
                </Card>

                {/* ── Change Plan Card ── */}
                <Card>
                    <CardHeader>
                        <ArrowsClockwise size={22} weight="duotone" />
                        <CardTitle>Change Plan</CardTitle>
                    </CardHeader>

                    <BillingToggle>
                        <BillingOption $isActive={billingCycle === 'monthly'} onClick={() => setBillingCycle('monthly')}>
                            Monthly
                        </BillingOption>
                        <BillingOption $isActive={billingCycle === 'yearly'} onClick={() => setBillingCycle('yearly')}>
                            Yearly (Save 20%)
                        </BillingOption>
                    </BillingToggle>

                    <PlansGrid>
                        {(['starter', 'home', 'plus', 'pro'] as const).map(planId => {
                            const plan = PLAN_CONFIGS[planId];
                            const isCurrent = currentPlanId === planId;
                            const price = billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly;

                            return (
                                <PlanCard key={planId} $isCurrent={isCurrent} $isPopular={plan.popular}>
                                    <PlanCardName>
                                        {getPlanIcon(planId)}
                                        {plan.name}
                                        {isCurrent && <CurrentBadge>Current</CurrentBadge>}
                                        {plan.popular && !isCurrent && <PopularBadge>Popular</PopularBadge>}
                                    </PlanCardName>
                                    <PlanCardTagline>{plan.tagline}</PlanCardTagline>
                                    <PlanCardPrice>
                                        {price === 0 ? 'Free' : `$${price}`}
                                        {price > 0 && <span>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>}
                                    </PlanCardPrice>
                                    <PlanSelectButton
                                        $isCurrent={isCurrent}
                                        disabled={isCurrent || checkoutLoading !== null}
                                        onClick={() => handlePlanSelect(planId)}
                                    >
                                        {checkoutLoading === planId ? 'Redirecting...' :
                                            isCurrent ? 'Current Plan' :
                                                planId === 'starter' ? 'Downgrade' : 'Upgrade'}
                                        {!isCurrent && checkoutLoading !== planId && <ArrowRight size={14} />}
                                    </PlanSelectButton>
                                </PlanCard>
                            );
                        })}
                    </PlansGrid>

                    <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                        <a href="mailto:info@sparkskool.com" style={{ color: '#8b949e', fontSize: '0.85rem', textDecoration: 'none' }}>
                            Need Enterprise? <span style={{ color: '#3AB8FF' }}>Contact our sales team →</span>
                        </a>
                    </div>
                </Card>

                {/* ── Account Info ── */}
                <Card>
                    <CardHeader>
                        <SignOut size={22} weight="duotone" />
                        <CardTitle>Account</CardTitle>
                    </CardHeader>
                    <InfoRow>
                        <InfoLabel>Email</InfoLabel>
                        <InfoValue>{user?.email}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>User ID</InfoLabel>
                        <InfoValue style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#8b949e' }}>{user?.uid}</InfoValue>
                    </InfoRow>
                    <ButtonRow>
                        <GhostButton onClick={logout}>
                            <SignOut size={18} />
                            Sign Out
                        </GhostButton>
                    </ButtonRow>
                </Card>
            </Container>

            {/* ── Cancel Confirmation Modal ── */}
            <AnimatePresence>
                {showCancelModal && (
                    <ModalOverlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowCancelModal(false)}
                    >
                        <ModalContent
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            <ModalTitle>
                                <Warning size={22} weight="fill" color="#f85149" />
                                Cancel Subscription?
                            </ModalTitle>
                            <ModalText>
                                Are you sure you want to cancel your <strong style={{ color: '#f0f6fc' }}>{currentPlan.name}</strong> plan?
                                Your access will continue until the end of your current billing period ({formatDate(subscription?.endDate)}).
                                You can re-subscribe at any time.
                            </ModalText>
                            <ModalActions>
                                <GhostButton onClick={() => setShowCancelModal(false)} style={{ flex: 1, justifyContent: 'center' }}>
                                    Keep Plan
                                </GhostButton>
                                <DangerButton
                                    onClick={handleCancelSubscription}
                                    disabled={cancelLoading}
                                    style={{ flex: 1, justifyContent: 'center' }}
                                >
                                    {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
                                </DangerButton>
                            </ModalActions>
                        </ModalContent>
                    </ModalOverlay>
                )}
            </AnimatePresence>

            <AuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
                onSuccess={() => setIsAuthModalOpen(false)}
            />
        </PageWrapper>
    );
};

export default AccountPage;
